export type BrowserCacheLeaseStatus = 'owned' | 'blocked' | 'idle';

export interface BrowserCacheLeaseState {
  status: BrowserCacheLeaseStatus;
  resourceId: string;
  ownerId: string | null;
  leaseUntil: number | null;
  channelAvailable: boolean;
}

export interface BrowserCacheLeaseClaimResult {
  ok: boolean;
  state: BrowserCacheLeaseState;
  blockedBy?: {
    ownerId: string;
    leaseUntil: number;
  };
}

export interface BrowserCacheInvalidationEvent {
  resourceId: string;
  ownerId: string;
  tables: string[];
  reason?: string;
  at: number;
}

export interface BrowserCacheLeaseCoordinatorOptions {
  channelName: string;
  resourceId: string;
  ownerId?: string;
  leaseTtlMs?: number;
  heartbeatMs?: number;
  probeWaitMs?: number;
  now?: () => number;
  channelFactory?: (name: string) => BroadcastChannelLike;
}

interface BroadcastChannelLike {
  onmessage: ((event: { data: BrowserCacheLeaseMessage }) => void) | null;
  postMessage(message: BrowserCacheLeaseMessage): void;
  close(): void;
}

type BrowserCacheLeaseMessage =
  | { type: 'probe'; resourceId: string; ownerId: string; at: number }
  | { type: 'claim'; resourceId: string; ownerId: string; leaseUntil: number; at: number }
  | { type: 'heartbeat'; resourceId: string; ownerId: string; leaseUntil: number; at: number }
  | { type: 'release'; resourceId: string; ownerId: string; at: number }
  | { type: 'invalidate'; resourceId: string; ownerId: string; tables: string[]; reason?: string; at: number };

type Listener<T> = (event: T) => void;

export class BrowserCacheLeaseCoordinator {
  readonly ownerId: string;

  private readonly resourceId: string;
  private readonly leaseTtlMs: number;
  private readonly heartbeatMs: number;
  private readonly probeWaitMs: number;
  private readonly now: () => number;
  private readonly channel: BroadcastChannelLike | null;
  private heartbeatTimer: any = null;
  private localLeaseUntil: number | null = null;
  private remoteOwnerId: string | null = null;
  private remoteLeaseUntil: number | null = null;
  private stateListeners = new Set<Listener<BrowserCacheLeaseState>>();
  private invalidationListeners = new Set<Listener<BrowserCacheInvalidationEvent>>();

  constructor(options: BrowserCacheLeaseCoordinatorOptions) {
    this.resourceId = options.resourceId;
    this.leaseTtlMs = options.leaseTtlMs ?? 15000;
    this.heartbeatMs = options.heartbeatMs ?? Math.max(1000, Math.floor(this.leaseTtlMs / 3));
    this.probeWaitMs = options.probeWaitMs ?? 75;
    this.now = options.now ?? (() => Date.now());
    this.ownerId = options.ownerId || createOwnerId();
    this.channel = this.createChannel(options);
    if (this.channel) {
      this.channel.onmessage = (event) => this.handleMessage(event.data);
    }
  }

  getState(): BrowserCacheLeaseState {
    this.pruneRemoteLease();
    if (this.localLeaseUntil && this.localLeaseUntil > this.now()) {
      return {
        status: 'owned',
        resourceId: this.resourceId,
        ownerId: this.ownerId,
        leaseUntil: this.localLeaseUntil,
        channelAvailable: !!this.channel,
      };
    }
    if (this.remoteOwnerId && this.remoteLeaseUntil && this.remoteLeaseUntil > this.now()) {
      return {
        status: 'blocked',
        resourceId: this.resourceId,
        ownerId: this.remoteOwnerId,
        leaseUntil: this.remoteLeaseUntil,
        channelAvailable: !!this.channel,
      };
    }
    return {
      status: 'idle',
      resourceId: this.resourceId,
      ownerId: null,
      leaseUntil: null,
      channelAvailable: !!this.channel,
    };
  }

  onState(listener: Listener<BrowserCacheLeaseState>): () => void {
    this.stateListeners.add(listener);
    listener(this.getState());
    return () => this.stateListeners.delete(listener);
  }

  onInvalidation(listener: Listener<BrowserCacheInvalidationEvent>): () => void {
    this.invalidationListeners.add(listener);
    return () => this.invalidationListeners.delete(listener);
  }

  async claim(signal?: AbortSignal): Promise<BrowserCacheLeaseClaimResult> {
    if (signal?.aborted) {
      throw new DOMException('Lease claim aborted', 'AbortError');
    }

    this.pruneRemoteLease();
    if (!this.hasActiveRemoteOwner()) {
      this.post({ type: 'probe', resourceId: this.resourceId, ownerId: this.ownerId, at: this.now() });
      await delay(this.probeWaitMs, signal);
      this.pruneRemoteLease();
    }

    if (this.hasActiveRemoteOwner()) {
      const state = this.getState();
      return {
        ok: false,
        state,
        blockedBy: {
          ownerId: this.remoteOwnerId || '',
          leaseUntil: this.remoteLeaseUntil || 0,
        },
      };
    }

    this.localLeaseUntil = this.now() + this.leaseTtlMs;
    this.post({
      type: 'claim',
      resourceId: this.resourceId,
      ownerId: this.ownerId,
      leaseUntil: this.localLeaseUntil,
      at: this.now(),
    });
    this.startHeartbeat();
    this.emitState();
    return { ok: true, state: this.getState() };
  }

  release(): void {
    if (!this.localLeaseUntil) {
      return;
    }
    this.localLeaseUntil = null;
    this.stopHeartbeat();
    this.post({ type: 'release', resourceId: this.resourceId, ownerId: this.ownerId, at: this.now() });
    this.emitState();
  }

  broadcastInvalidation(tables: string[], reason?: string): void {
    const message: BrowserCacheLeaseMessage = {
      type: 'invalidate',
      resourceId: this.resourceId,
      ownerId: this.ownerId,
      tables: Array.from(new Set((tables || []).filter(Boolean))),
      reason,
      at: this.now(),
    };
    this.post(message);
    this.emitInvalidation(message);
  }

  dispose(): void {
    this.release();
    this.channel?.close();
    this.stateListeners.clear();
    this.invalidationListeners.clear();
  }

  private createChannel(options: BrowserCacheLeaseCoordinatorOptions): BroadcastChannelLike | null {
    try {
      if (options.channelFactory) {
        return options.channelFactory(options.channelName);
      }
      const ChannelCtor = (globalThis as any).BroadcastChannel;
      if (!ChannelCtor) {
        return null;
      }
      return new ChannelCtor(options.channelName) as BroadcastChannelLike;
    } catch {
      return null;
    }
  }

  private handleMessage(message: BrowserCacheLeaseMessage): void {
    if (!message || message.resourceId !== this.resourceId || message.ownerId === this.ownerId) {
      return;
    }

    if (message.type === 'probe') {
      if (this.localLeaseUntil && this.localLeaseUntil > this.now()) {
        this.post({
          type: 'heartbeat',
          resourceId: this.resourceId,
          ownerId: this.ownerId,
          leaseUntil: this.localLeaseUntil,
          at: this.now(),
        });
      }
      return;
    }

    if (message.type === 'claim' || message.type === 'heartbeat') {
      if (message.leaseUntil > this.now()) {
        this.remoteOwnerId = message.ownerId;
        this.remoteLeaseUntil = message.leaseUntil;
        this.emitState();
      }
      return;
    }

    if (message.type === 'release') {
      if (this.remoteOwnerId === message.ownerId) {
        this.remoteOwnerId = null;
        this.remoteLeaseUntil = null;
        this.emitState();
      }
      return;
    }

    if (message.type === 'invalidate') {
      this.emitInvalidation(message);
    }
  }

  private hasActiveRemoteOwner(): boolean {
    return !!this.remoteOwnerId && !!this.remoteLeaseUntil && this.remoteLeaseUntil > this.now();
  }

  private pruneRemoteLease(): void {
    if (this.remoteLeaseUntil && this.remoteLeaseUntil <= this.now()) {
      this.remoteOwnerId = null;
      this.remoteLeaseUntil = null;
      this.emitState();
    }
    if (this.localLeaseUntil && this.localLeaseUntil <= this.now()) {
      this.localLeaseUntil = null;
      this.stopHeartbeat();
      this.emitState();
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this.localLeaseUntil) {
        this.stopHeartbeat();
        return;
      }
      this.localLeaseUntil = this.now() + this.leaseTtlMs;
      this.post({
        type: 'heartbeat',
        resourceId: this.resourceId,
        ownerId: this.ownerId,
        leaseUntil: this.localLeaseUntil,
        at: this.now(),
      });
      this.emitState();
    }, this.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  private post(message: BrowserCacheLeaseMessage): void {
    this.channel?.postMessage(message);
  }

  private emitState(): void {
    const state = this.getState();
    for (const listener of this.stateListeners) {
      listener(state);
    }
  }

  private emitInvalidation(message: Extract<BrowserCacheLeaseMessage, { type: 'invalidate' }>): void {
    const event: BrowserCacheInvalidationEvent = {
      resourceId: message.resourceId,
      ownerId: message.ownerId,
      tables: message.tables,
      reason: message.reason,
      at: message.at,
    };
    for (const listener of this.invalidationListeners) {
      listener(event);
    }
  }
}

function createOwnerId(): string {
  const cryptoObj = (globalThis as any).crypto;
  if (cryptoObj?.randomUUID) {
    return cryptoObj.randomUUID();
  }
  return `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Lease claim aborted', 'AbortError'));
      return;
    }
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeout);
      reject(new DOMException('Lease claim aborted', 'AbortError'));
    };
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}
